import type {MouseEvent, ReactElement} from 'react';
import type {VoiceProfileItemProps} from './voice-profile-item.types';
import {Text, TextSize, TextType} from '@Components/text';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {ClickableDiv} from '@Components/clickable-div';
import styles from './voice-profile-item.module.scss';
import {useAudioPlayback} from '@Hooks/ai-text-to-speech/useAudioPlayback';
import {PlayPauseButton} from '@Panels/ai-voice-panel/components/play-pause-button/play-pause-button';

export function VoiceProfileItem({profile, isSelected, ...props}: VoiceProfileItemProps): ReactElement {
  const audioPlayback = useAudioPlayback();
  const avatarUrl = profile.avatarUrl ?? getRandomThumbnailImageForAudioItem();

  const handlePlayPauseClick = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    audioPlayback.togglePlayPause(profile.soundtrack);
  };

  return (
    <ClickableDiv
      className={`flex-center radius-8 gap-3 spacing-p-t-1 spacing-p-b-1 spacing-p-l-2 spacing-p-r-2 _cursor-pointer hover-transition-all ${styles.profileItem} ${isSelected ? styles.profileItemSelected : ''}`}
      onClick={() => {
        props.onProfileSelected(profile.id);
      }}
    >
      <div className={`flex-center _position-relative radius-8 ${styles.avatarContainer}`}>
        <img className={`_position-absolute ${styles.avatar}`} src={avatarUrl} alt={profile.name} loading="lazy" />
      </div>
      <div className={'flexbox flex-1 flex-column'}>
        <Text className={isSelected ? 'content-primary' : 'content-body'} bold size={TextSize.XSMALL} type={TextType.BODY} val={profile.name} />
        <Text size={TextSize.XXSMALL} className={isSelected ? 'content-primary' : 'content-body'} val={profile.description} />
      </div>
      <PlayPauseButton
        isPlaying={audioPlayback.isPlaying}
        playbackProgress={audioPlayback.playbackProgress}
        onClick={handlePlayPauseClick}
        baseColor={PMW_COLORS_NEUTRAL.NEUTRAL_6}
        className={styles.playButton}
      />
    </ClickableDiv>
  );
}
